/**
 * StatBand - stat-per-claim trust band (Editorial Authority S3).
 * parseStatNum + Stat EXTRACTED tu routes/landing.tsx (no-duplicate-code
 * rule s2026-05-13). Percent branch extended to keep trailing "+" suffix.
 *
 * SSR-safe: gia tri cuoi render thang trong static markup (KHONG "0+"),
 * client animate count-up sau reveal.
 *
 * CSS classes da co trong landing-editorial.css:
 *   ed-statband, ed-stat, ed-stat__num, ed-stat__label, ed-statband__note
 * KHONG them CSS moi.
 */
import { useEffect, useMemo, useState } from 'react';
import { useReveal } from '../../lib/use-reveal';
import { animateCount } from '../../lib/animate-count';

export function parseStatNum(raw: string): {
  value: number;
  format: (n: number) => string;
  animate: boolean;
} {
  const t = raw.trim();
  // "< 2s" etc -> static (leading < without =)
  if (t.startsWith('<') && t[1] !== '=') {
    return { value: 0, format: () => raw, animate: false };
  }
  const pm = t.match(/^([<>]=?|~)\s*/);
  const prefix = pm ? pm[0] : '';
  const rest = pm ? t.slice(pm[0].length) : t;

  // e.g. "4.8M" "4.8M+"
  const mm = rest.match(/^([\d.]+)M([+]?)$/);
  if (mm) {
    const base = parseFloat(mm[1] ?? '');
    if (!Number.isFinite(base)) return { value: 0, format: () => raw, animate: false };
    const tail = mm[2] ?? '';
    return {
      value: Math.round(base * 1e6),
      format: (n) => `${prefix}${(n / 1e6).toFixed(1)}M${tail}`,
      animate: true,
    };
  }

  // e.g. "95%" or "95%+" - percent with optional trailing "+"
  const pc = rest.match(/^([\d.]+)%([+]?)$/);
  if (pc) {
    const base = parseFloat(pc[1] ?? '');
    if (!Number.isFinite(base)) return { value: 0, format: () => raw, animate: false };
    const tail = pc[2] ?? '';
    return {
      value: base,
      format: (n) => `${prefix}${Math.round(n)}%${tail}`,
      animate: true,
    };
  }

  // e.g. "160,000+" "34"
  const im = rest.match(/^([\d,]+)([+]?)$/);
  if (im) {
    const base = parseInt((im[1] ?? '').replace(/,/g, ''), 10);
    if (!Number.isFinite(base)) return { value: 0, format: () => raw, animate: false };
    const tail = im[2] ?? '';
    return {
      value: base,
      format: (n) => `${prefix}${Math.round(n).toLocaleString('en-US')}${tail}`,
      animate: true,
    };
  }

  return { value: 0, format: () => raw, animate: false };
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function Stat({ num, label }: { num: string; label: string }) {
  const parsed = useMemo(() => parseStatNum(num), [num]);
  const [ref, revealed] = useReveal<HTMLDivElement>(0.15);
  const [mounted, setMounted] = useState(false);

  // SSR-safe: finalDisplay is the final formatted value (e.g. "160,000+"),
  // used as initial state so SSR markup never contains "0+".
  const finalDisplay = parsed.animate ? parsed.format(parsed.value) : num;
  const [display, setDisplay] = useState<string>(finalDisplay);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!parsed.animate || !mounted || !revealed) return;
    if (prefersReducedMotion()) {
      setDisplay(parsed.format(parsed.value));
      return;
    }
    setDisplay(parsed.format(0));
    const cleanup = animateCount(0, parsed.value, 1200, (v) => {
      setDisplay(parsed.format(v));
    });
    return cleanup;
  }, [parsed, revealed, mounted]);

  return (
    <div ref={ref} className="ed-stat">
      <span className="ed-stat__num">{parsed.animate ? display : num}</span>
      <span className="ed-stat__label">{label}</span>
    </div>
  );
}

export interface StatItem {
  num: string;
  label: string;
}

export function StatBand({ stats, note }: { stats: StatItem[]; note: string }) {
  return (
    <div>
      <div className="ed-statband">
        {stats.map((s) => (
          <Stat key={s.label} num={s.num} label={s.label} />
        ))}
      </div>
      <p className="ed-statband__note">{note}</p>
    </div>
  );
}
